import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, User, CheckCircle, AlertCircle, Loader2, Save, Sparkles,
  FileText, BookOpen, X, Info
} from 'lucide-react';
import { authenticatedFetch } from '@/lib/auth';
import { useToast, useConfirm } from './ui';
import { parseAssessmentFormShort, getAssessmentFormBadgeClass } from '@/lib/assessmentForm';

interface Candidate {
  session_id: string;
  candidate_code: string;
  anonymous_id: string;
  status: string;
  started_at: string;
  submitted_at: string | null;
  exam_title: string;
  percentage: number | null;
  passed: boolean | null;
  grade: string | null;
  total_answers: number;
  graded_answers: number;
  // v6.3.6: sensur-detaljer
  alternative_assessment?: boolean;
  alternative_assessment_note?: string | null;
  sensor_comment?: string | null;
  sensor_name?: string | null;
  sensored_at?: string | null;
}

// DL-1/DL-2: prosjekt/portfolio/muntlig-innleveringer (ikke-SEB)
interface ProjectSubmission {
  submission_id: string;
  status: string;
  submitted_at: string | null;
  sensor_graded_at: string | null;
  sensor_score: number | null;
  sensor_grade: string | null;
  sensor_passed: boolean | null;
  alternative_assessment_used: boolean | null;
  course_candidate_id: string;
  candidate_name: string | null;
  candidate_code: string | null;
  component_id: string;
  component_type: string;
  component_title: string;
  component_sequence: number;
  is_anonymous: boolean;
  file_count: number | null;
  sensor_name: string | null;
}

const COMPONENT_TYPE_LABELS: Record<string, string> = {
  project: 'Prosjekt',
  portfolio: 'Mappe',
  group: 'Gruppe',
  oral: 'Muntlig',
  practical: 'Praktisk',
  observation: 'Observasjon',
  reflection: 'Refleksjon'
};

interface Answer {
  answer_id: string;
  question_text: string;
  question_type: string;
  max_points: number;
  candidate_answer: string;
  selected_option: number | null;
  options?: Array<{ text: string; is_correct: boolean }>;
  grading_criteria?: string;
  sensor_score: number | null;
  sensor_feedback: string | null;
  ai_suggested_score: number | null;
  ai_suggested_feedback: string | null;
  ai_model: string | null;
}

interface CourseContext {
  course_id: string;
  course_name: string;
  start_date: string;
  end_date: string;
  exam_set_id: string;
  exam_title: string;
  exam_description: string;
  pass_threshold: number;
  grading_scale: 'pass_fail' | 'three_tier' | 'a_f' | 'percentage';
  study_plan_id: string;
  study_plan_title: string;
  ects_credits: number;
  estimated_hours: number;
  learning_outcomes: {
    knowledge: string[];
    skills: string[];
    competence: string[];
  };
  content_modules: Array<{
    title: string;
    description: string;
    hours: number;
  }>;
  assessment_criteria: string;
  metadata: any;
  total_questions: number;
  total_points: number;
}

interface QuestionStatistics {
  question_id: string;
  question_text: string;
  question_type: string;
  max_points: number;
  total_answers: number;
  graded_count: number;
  avg_score: string | null;
  min_score: string | null;
  max_score: string | null;
  std_dev: string | null;
}

// Sensor grading interface with KI assistance
export default function SensorGrading() {
  const { courseId } = useParams<{ courseId: string }>();
  const navigate = useNavigate();
  const { confirm } = useConfirm();

  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [projectSubmissions, setProjectSubmissions] = useState<ProjectSubmission[]>([]);
  const [selectedCandidate, setSelectedCandidate] = useState<Candidate | null>(null);
  const [answers, setAnswers] = useState<Answer[]>([]);
  const [currentAnswerIndex, setCurrentAnswerIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingAnswers, setLoadingAnswers] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [generatingAI, setGeneratingAI] = useState(false);
  const [courseContext, setCourseContext] = useState<CourseContext | null>(null);
  const [showContextModal, setShowContextModal] = useState(false);
  const [statistics, setStatistics] = useState<QuestionStatistics[]>([]);

  // v6.3.1 — Alternativ vurderings-modal (sensor-override)
  const [showAlternativeModal, setShowAlternativeModal] = useState(false);
  const [altAssessmentType, setAltAssessmentType] = useState<'oral' | 'observation' | 'practical' | 'other'>('oral');
  const [altGrade, setAltGrade] = useState('');
  const [altJustification, setAltJustification] = useState('');
  const [altConfirmChecked, setAltConfirmChecked] = useState(false);
  const [savingAlternative, setSavingAlternative] = useState(false);
  const [finalizingGrading, setFinalizingGrading] = useState(false);

  // v6.3.8 — Fullfør sensur når alle svar har sensor_score
  const handleFinalizeGrading = async (includeMissingAsZero = false) => {
    if (!selectedCandidate) return;
    if (!includeMissingAsZero) {
      const confirmed = await confirm({
        title: `Fullfør sensur — ${selectedCandidate.candidate_code}`,
        message:
          'Ved å fullføre sensuren nå:\n' +
          '• Endelig karakter beregnes basert på sensurerte svar\n' +
          '• Resultatet låses (kan kun endres av systemadmin)\n' +
          '• Kandidaten mottar e-post + SMS med resultat og claim-lenke til kompetanseattest',
        confirmLabel: 'Ja, fullfør og send resultat',
        cancelLabel: 'Avbryt',
        variant: 'default'
      });
      if (!confirmed) return;
    }

    setFinalizingGrading(true);
    try {
      const response = await authenticatedFetch(
        `/api/sensor/session/${selectedCandidate.session_id}/finalize-grading`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(includeMissingAsZero ? { include_missing_as_zero: true } : {})
        }
      );
      const data = await response.json();
      if (!data.success) {
        // Håndter "missing_answers"-feil med et valg
        if (data.error === 'missing_answers' && !includeMissingAsZero) {
          const choice = await confirm({
            title: `${data.missing_count} spørsmål har ingen svar fra kandidaten`,
            message:
              `${data.message}\n\n` +
              `Klikk "Fullfør med 0 poeng" hvis de ubesvarte spørsmålene skal telles som 0 poeng ` +
              `(gir sannsynlig ikke bestått).\n\n` +
              `Klikk Avbryt for å gå tilbake og bruke "Registrer alternativ vurdering" i stedet — ` +
              `anbefalt hvis kandidaten ble vurdert muntlig eller mistet SEB-forbindelsen.`,
            confirmLabel: `Fullfør med 0 poeng på ${data.missing_count} ubesvarte`,
            cancelLabel: 'Avbryt — bruk alternativ vurdering',
            variant: 'warning'
          });
          setFinalizingGrading(false);
          if (choice) return handleFinalizeGrading(true);
          return;
        }
        throw new Error(data.message || data.error || 'Kunne ikke fullføre sensur');
      }
      setSuccess(data.message || 'Sensur fullført. Kandidat mottar varsel.');
      // v6.3.14: Oppdater selectedCandidate i minne så knapper skjules umiddelbart
      // (fetchCandidates oppdaterer listen, men ikke referansen til selectedCandidate).
      setSelectedCandidate(prev => prev ? { ...prev, status: 'graded', grade: data.result?.grade ?? prev.grade, passed: data.result?.passed ?? prev.passed, percentage: data.result?.percentage ?? prev.percentage } : prev);
      fetchCandidates();
      setTimeout(() => setSuccess(''), 8000);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setFinalizingGrading(false);
    }
  };

  // Hent karakter-alternativer basert på prøvesettets skala (samme sannhet som normal sensur)
  const getGradeOptionsForScale = (scale?: string) => {
    switch (scale) {
      case 'pass_fail':
        return { passing: ['Bestått'], failing: ['Ikke bestått'], numeric: false };
      case 'three_tier':
        return { passing: ['Meget godt bestått', 'Bestått'], failing: ['Ikke bestått'], numeric: false };
      case 'a_f':
        return { passing: ['A', 'B', 'C', 'D', 'E'], failing: ['F'], numeric: false };
      case 'percentage':
        return { passing: [], failing: [], numeric: true };
      default:
        return null;
    }
  };

  const gradeOpts = getGradeOptionsForScale(courseContext?.grading_scale);
  const allGrades = gradeOpts && !gradeOpts.numeric ? [...gradeOpts.passing, ...gradeOpts.failing] : [];
  const isSelectedPassing = gradeOpts?.numeric
    ? (parseInt(altGrade, 10) >= (courseContext?.pass_threshold ?? 100))
    : (gradeOpts?.passing.includes(altGrade) ?? false);

  // Reset karakter til første gyldige når modal åpnes eller skala endres
  useEffect(() => {
    if (showAlternativeModal && gradeOpts && !gradeOpts.numeric && allGrades.length > 0 && !allGrades.includes(altGrade)) {
      setAltGrade(gradeOpts.passing[0] || allGrades[0]);
    }
    if (showAlternativeModal && gradeOpts?.numeric && !altGrade) {
      setAltGrade(String(courseContext?.pass_threshold ?? 70));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAlternativeModal, courseContext?.grading_scale]);

  const handleSubmitAlternative = async () => {
    if (!selectedCandidate) return;
    if (!altGrade) {
      setError('Karakter må velges.');
      return;
    }
    if (altJustification.trim().length < 100) {
      setError('Begrunnelse må være minst 100 tegn.');
      return;
    }
    if (!altConfirmChecked) {
      setError('Du må bekrefte at du har gjennomført alternativ vurdering før den kan registreres.');
      return;
    }
    setSavingAlternative(true);
    try {
      const response = await authenticatedFetch(
        `/api/sensor/session/${selectedCandidate.session_id}/override-alternative`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            grade: altGrade,
            assessment_type: altAssessmentType,
            justification: altJustification.trim()
          })
        }
      );
      const data = await response.json();
      if (!data.success) {
        throw new Error(data.message || data.error || 'Kunne ikke registrere alternativ vurdering');
      }
      setSuccess(data.message || 'Alternativ vurdering registrert.');
      setShowAlternativeModal(false);
      setAltJustification('');
      setAltConfirmChecked(false);
      // Reload for å se resultatet
      fetchCandidates();
      setTimeout(() => setSuccess(''), 5000);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSavingAlternative(false);
    }
  };

  useEffect(() => {
    if (courseId) {
      fetchCandidates();
      fetchCourseContext();
      fetchStatistics();
    }
  }, [courseId]);

  const fetchCandidates = async () => {
    try {
      setLoading(true);
      const response = await authenticatedFetch(`/api/sensor/candidates/${courseId}`);
      const data = await response.json();

      if (data.success) {
        setCandidates(data.candidates);
        setProjectSubmissions(Array.isArray(data.project_submissions) ? data.project_submissions : []);
      } else {
        setError(data.message || 'Kunne ikke hente kandidater');
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchCourseContext = async () => {
    try {
      const response = await authenticatedFetch(`/api/sensor/course/${courseId}/context`);
      const data = await response.json();

      if (data.success) {
        setCourseContext(data.context);
      } else {
        console.error('Could not fetch course context:', data.message);
      }
    } catch (err: any) {
      console.error('Error fetching course context:', err.message);
    }
  };

  const fetchStatistics = async () => {
    try {
      const response = await authenticatedFetch(`/api/sensor/course/${courseId}/statistics`);
      const data = await response.json();

      if (data.success) {
        setStatistics(data.statistics);
      } else {
        console.error('Could not fetch statistics:', data.message);
      }
    } catch (err: any) {
      console.error('Error fetching statistics:', err.message);
    }
  };

  const fetchAnswers = async (sessionId: string) => {
    try {
      setLoadingAnswers(true);
      const response = await authenticatedFetch(`/api/sensor/session/${sessionId}/answers`);
      const data = await response.json();

      if (data.success) {
        setAnswers(data.answers);
      } else {
        setError(data.message || 'Kunne ikke hente svar');
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoadingAnswers(false);
    }
  };

  const selectCandidate = (candidate: Candidate) => {
    setSelectedCandidate(candidate);
    setCurrentAnswerIndex(0);
    fetchAnswers(candidate.session_id);
  };

  const goToNextAnswer = () => {
    if (currentAnswerIndex < answers.length - 1) {
      setCurrentAnswerIndex(currentAnswerIndex + 1);
    }
  };

  const goToPreviousAnswer = () => {
    if (currentAnswerIndex > 0) {
      setCurrentAnswerIndex(currentAnswerIndex - 1);
    }
  };

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (!selectedCandidate || answers.length === 0) return;

      if (e.key === 'n' && !e.ctrlKey && !e.metaKey) {
        const activeElement = document.activeElement as HTMLElement;
        if (activeElement.tagName !== 'INPUT' && activeElement.tagName !== 'TEXTAREA') {
          e.preventDefault();
          goToNextAnswer();
        }
      } else if (e.key === 'p' && !e.ctrlKey && !e.metaKey) {
        const activeElement = document.activeElement as HTMLElement;
        if (activeElement.tagName !== 'INPUT' && activeElement.tagName !== 'TEXTAREA') {
          e.preventDefault();
          goToPreviousAnswer();
        }
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [selectedCandidate, answers, currentAnswerIndex]);

  const handleGenerateAI = async () => {
    if (!selectedCandidate) return;

    try {
      setGeneratingAI(true);
      setError('');
      const response = await authenticatedFetch(
        `/api/sensor/generate-ai-grading/${selectedCandidate.session_id}`,
        { method: 'POST' }
      );
      const data = await response.json();

      if (data.success) {
        setSuccess(`KI-sensurutkast generert for ${data.graded} svar`);
        fetchAnswers(selectedCandidate.session_id);
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(data.message || 'Kunne ikke generere KI-sensurutkast');
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setGeneratingAI(false);
    }
  };

  const handleSaveGrading = async (answerId: string, score: number, feedback: string, acceptAI: boolean) => {
    try {
      setError('');
      const response = await authenticatedFetch(`/api/sensor/save-grading/${answerId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          score,
          feedback,
          accept_ai_suggestion: acceptAI
        })
      });

      const data = await response.json();

      if (data.success) {
        setSuccess('Sensurering lagret');
        if (selectedCandidate) {
          fetchAnswers(selectedCandidate.session_id);
          fetchCandidates(); // Refresh counts
        }
        if (data.all_graded) {
          setSuccess('Alle svar sensurert! Prøven er nå fullført.');
        }
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(data.message || 'Kunne ikke lagre sensurering');
      }
    } catch (err: any) {
      setError(err.message);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-kf-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/sensor/dashboard')}
          className="p-2 hover:bg-gray-100 rounded-lg"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Sensurering</h2>
          <p className="text-sm text-gray-600">Gjennomgå og sensurer kandidatbesvarelser</p>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg flex items-center gap-2">
          <AlertCircle className="h-5 w-5" />
          <span>{error}</span>
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg flex items-center gap-2">
          <CheckCircle className="h-5 w-5" />
          <span>{success}</span>
        </div>
      )}

      {/* DL-2: Prosjekt/portfolio/muntlig-innleveringer — klikkbare til SensorProjectView.
          Vises ved siden av SEB-kandidat-listen når kurset har slike komponenter. */}
      {projectSubmissions.length > 0 && (
        <div className="bg-white border rounded-lg overflow-hidden mb-6">
          <div className="p-4 border-b bg-purple-50">
            <h3 className="font-semibold text-gray-900 flex items-center gap-2">
              <FileText className="h-4 w-4 text-purple-700" />
              Prosjekt/innleveringer ({projectSubmissions.length})
            </h3>
            <p className="text-xs text-gray-600 mt-1">
              Klikk en innlevering for å åpne vurderingsvisningen.
            </p>
          </div>
          <div className="divide-y max-h-[400px] overflow-y-auto">
            {projectSubmissions.map(sub => {
              const displayName = sub.is_anonymous
                ? (sub.candidate_code || 'Anonym')
                : (sub.candidate_name || sub.candidate_code || 'Ukjent');
              const canOpen = sub.status !== 'not_started';
              return (
                <div
                  key={sub.submission_id}
                  onClick={() => canOpen && navigate(`/sensor/project-submissions/${sub.submission_id}`)}
                  className={`p-3 flex items-center justify-between gap-3 ${canOpen ? 'cursor-pointer hover:bg-gray-50' : 'opacity-60'} transition-colors`}
                >
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <div className="p-2 bg-purple-100 rounded flex-shrink-0">
                      <FileText className="h-4 w-4 text-purple-700" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-gray-900 truncate">{displayName}</div>
                      <div className="text-xs text-gray-600">
                        {COMPONENT_TYPE_LABELS[sub.component_type] || sub.component_type} · {sub.component_title}
                      </div>
                      {sub.submitted_at && (
                        <div className="text-xs text-gray-500 mt-0.5">
                          Levert: {new Date(sub.submitted_at).toLocaleString('nb-NO')}
                          {sub.file_count ? ` · ${sub.file_count} fil${sub.file_count === 1 ? '' : 'er'}` : ''}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex-shrink-0">
                    {sub.status === 'submitted' && (
                      <span className="px-2 py-0.5 bg-yellow-100 text-yellow-800 text-xs rounded-full">Venter</span>
                    )}
                    {sub.status === 'graded' && (
                      <span className="px-2 py-0.5 bg-green-100 text-green-800 text-xs rounded-full">
                        Sensurert{sub.sensor_grade ? ` · ${sub.sensor_grade}` : ''}
                      </span>
                    )}
                    {sub.status === 'overridden_by_alternative' && (
                      <span className="px-2 py-0.5 bg-purple-100 text-purple-800 text-xs rounded-full">Alt. vurdering</span>
                    )}
                    {sub.status === 'in_progress' && (
                      <span className="px-2 py-0.5 bg-blue-100 text-blue-800 text-xs rounded-full">Under arbeid</span>
                    )}
                    {sub.status === 'not_started' && (
                      <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full">Ikke påbegynt</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Candidate list (SEB-skriftlig prøver) */}
        <div className="lg:col-span-1">
          <div className="bg-white border rounded-lg overflow-hidden">
            <div className="p-4 border-b bg-gray-50">
              <h3 className="font-semibold text-gray-900">
                {projectSubmissions.length > 0 ? 'Skriftlig prøve (SEB)' : 'Kandidater'}
              </h3>
            </div>
            <div className="divide-y max-h-[600px] overflow-y-auto">
              {candidates.length === 0 ? (
                <div className="p-4 text-center text-gray-500 text-sm">
                  {projectSubmissions.length > 0
                    ? 'Dette kurset har ingen skriftlig SEB-prøve.'
                    : 'Ingen kandidater funnet'}
                </div>
              ) : (
                candidates.map((candidate) => {
                  const progress = candidate.total_answers > 0
                    ? (candidate.graded_answers / candidate.total_answers) * 100
                    : 0;

                  return (
                    <div
                      key={candidate.session_id}
                      onClick={() => selectCandidate(candidate)}
                      className={`p-4 cursor-pointer hover:bg-gray-50 transition-colors ${
                        selectedCandidate?.session_id === candidate.session_id ? 'bg-kf-blue-50' : ''
                      }`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4 text-gray-400" />
                          <span className="font-medium text-sm">
                            {candidate.candidate_code}
                          </span>
                        </div>
                        {candidate.status === 'submitted' && (
                          <span className="px-2 py-0.5 bg-yellow-100 text-yellow-800 text-xs rounded-full">
                            Venter
                          </span>
                        )}
                        {candidate.status === 'graded' && (
                          <span className="px-2 py-0.5 bg-green-100 text-green-800 text-xs rounded-full">
                            Sensurert
                          </span>
                        )}
                        {candidate.status === 'graded' && candidate.alternative_assessment && (() => {
                          const label = parseAssessmentFormShort(candidate.alternative_assessment_note);
                          const colorClass = getAssessmentFormBadgeClass(candidate.alternative_assessment_note);
                          return label ? (
                            <span
                              className={`px-2 py-0.5 border text-xs rounded-full font-medium ${colorClass}`}
                              title="Vurderingsform benyttet"
                            >
                              {label}
                            </span>
                          ) : null;
                        })()}
                      </div>

                      <div className="text-xs text-gray-500 mb-2">
                        Innlevert: {candidate.submitted_at
                          ? new Date(candidate.submitted_at).toLocaleString('nb-NO')
                          : 'Pågår'}
                      </div>

                      {candidate.total_answers > 0 && (
                        <div>
                          <div className="flex justify-between text-xs text-gray-600 mb-1">
                            <span>Fremgang</span>
                            <span>{candidate.graded_answers}/{candidate.total_answers}</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-1.5">
                            <div
                              className="bg-kf-blue-600 h-1.5 rounded-full"
                              style={{ width: `${progress}%` }}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>

        {/* Grading panel */}
        <div className="lg:col-span-2">
          {!selectedCandidate ? (
            <div className="bg-white border rounded-lg p-12 text-center">
              <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Velg en kandidat
              </h3>
              <p className="text-gray-600">
                Velg en kandidat fra listen for å starte sensurering
              </p>
            </div>
          ) : loadingAnswers ? (
            <div className="flex items-center justify-center p-12">
              <Loader2 className="h-8 w-8 animate-spin text-kf-blue-600" />
            </div>
          ) : (
            <div className="space-y-4">
              {/* Candidate info and context */}
              <div className="bg-white border rounded-lg p-4">
                {/* v6.3.9: KI-knapp vises kun når det finnes åpne svar (essay/short_answer).
                    Multiple choice + true/false auto-graderes ved submit — KI-sensur har ingen nytte der. */}
                {(() => {
                  const openTypes = ['essay', 'short_answer', 'long_answer', 'text', 'open'];
                  const openAnswers = answers.filter(a => openTypes.includes(a.question_type));
                  const hasOpen = openAnswers.length > 0;
                  const openUngraded = openAnswers.filter(a => a.sensor_score === null).length;
                  return (
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="font-semibold text-lg">{selectedCandidate.candidate_code}</h3>
                        <p className="text-sm text-gray-600">{selectedCandidate.exam_title}</p>
                        {hasOpen && (
                          <p className="text-xs text-gray-500 mt-1">
                            Prøven har {openAnswers.length} åpne svar (essay/kortsvar) — {openUngraded} venter på sensur.
                          </p>
                        )}
                      </div>
                      {hasOpen && (
                        <button
                          onClick={handleGenerateAI}
                          disabled={generatingAI || openUngraded === 0}
                          className="flex items-center gap-2 px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
                          title={openUngraded === 0
                            ? 'Alle åpne svar er allerede sensurert'
                            : `KI foreslår score + tilbakemelding for ${openUngraded} åpne svar. Du kan akseptere, endre eller avvise forslagene før du lagrer. KI vurderer IKKE multiple choice/true-false — de auto-graderes ved innlevering.`}
                        >
                          {generatingAI ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                          Lag KI-sensurutkast ({openUngraded})
                        </button>
                      )}
                    </div>
                  );
                })()}
                {courseContext && (
                  <div className="pt-3 border-t">
                    <button
                      onClick={() => setShowContextModal(true)}
                      className="flex items-center gap-2 text-sm text-kf-blue-600 hover:text-kf-blue-700"
                    >
                      <BookOpen className="h-4 w-4" />
                      <span>Vis studieplan og sensureringskriterier</span>
                    </button>
                  </div>
                )}
                {/* v6.3.1 — Alternativ vurderings-knapp — skjules etter sensur (v6.3.14) */}
                {selectedCandidate.status !== 'graded' && (
                  <div className="pt-3 border-t">
                    <button
                      onClick={() => setShowAlternativeModal(true)}
                      className="flex items-center gap-2 text-sm text-purple-600 hover:text-purple-700 font-medium"
                      title="Bruk når kandidaten er vurdert muntlig, ved observasjon eller praktisk demonstrasjon i stedet for skriftlig prøve"
                    >
                      <Info className="h-4 w-4" />
                      <span>Registrer alternativ vurdering (muntlig / praktisk / observasjon)</span>
                    </button>
                  </div>
                )}

                {/* v6.3.8 — Fullfør sensur-knapp (vises når kandidat ikke er ferdig-sensurert enda) */}
                {selectedCandidate.status !== 'graded' && (
                  <div className="pt-3 border-t">
                    {(() => {
                      const totalGraded = answers.filter(a => a.sensor_score !== null).length;
                      const canFinalize = answers.length > 0 && totalGraded === answers.length;
                      return (
                        <div className={`rounded-lg p-3 ${canFinalize ? 'bg-green-50 border border-green-300' : 'bg-amber-50 border border-amber-300'}`}>
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex-1 text-sm">
                              <div className="font-medium mb-1">
                                {canFinalize
                                  ? '✅ Alle svar sensurert — klar til å fullføre'
                                  : `⏳ ${totalGraded} av ${answers.length} svar sensurert`}
                              </div>
                              <p className="text-xs text-gray-700">
                                {canFinalize
                                  ? 'Klikk under for å beregne endelig karakter og sende resultat til kandidat.'
                                  : 'Sensurer alle svar før du kan fullføre sensuren.'}
                              </p>
                            </div>
                            <button
                              onClick={() => handleFinalizeGrading()}
                              disabled={!canFinalize || finalizingGrading}
                              className={`px-4 py-2 rounded-lg text-white text-sm font-medium whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed ${
                                canFinalize ? 'bg-green-600 hover:bg-green-700' : 'bg-gray-400'
                              }`}
                              title={canFinalize
                                ? 'Beregn karakter, opprett attest-grunnlag, og send e-post/SMS til kandidat'
                                : 'Alle svar må være sensurert først'}
                            >
                              {finalizingGrading ? 'Fullfører…' : 'Fullfør sensur og send resultat'}
                            </button>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                )}

                {/* v6.3.6 — Sensur-detaljer for allerede sensurert kandidat */}
                {selectedCandidate.status === 'graded' && (
                  <div className={`mt-3 pt-3 border-t rounded-lg p-4 ${selectedCandidate.alternative_assessment ? 'bg-indigo-50 border border-indigo-200' : 'bg-kf-blue-50 border border-kf-blue-200'}`}>
                    <h4 className={`font-semibold mb-2 ${selectedCandidate.alternative_assessment ? 'text-indigo-900' : 'text-kf-blue-900'}`}>
                      {selectedCandidate.alternative_assessment
                        ? `Sensur-detaljer · Vurderingsform: ${parseAssessmentFormShort(selectedCandidate.alternative_assessment_note) || 'Annen'}`
                        : 'Sensur-detaljer'}
                    </h4>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                      <div><strong>Karakter:</strong> <span className={selectedCandidate.passed ? 'text-green-700' : 'text-red-700'}>{selectedCandidate.grade || '(ikke satt)'}</span></div>
                      <div><strong>Resultat:</strong> {selectedCandidate.passed ? '✅ Bestått' : '❌ Ikke bestått'}</div>
                      {selectedCandidate.percentage !== null && selectedCandidate.percentage !== undefined && (
                        <div><strong>Prosentscore:</strong> {selectedCandidate.percentage}%</div>
                      )}
                      {selectedCandidate.sensor_name && (
                        <div><strong>Sensor:</strong> {selectedCandidate.sensor_name}</div>
                      )}
                      {selectedCandidate.sensored_at && (
                        <div className="col-span-2"><strong>Sensurert:</strong> {new Date(selectedCandidate.sensored_at).toLocaleString('nb-NO', { timeZone: 'Europe/Oslo' })}</div>
                      )}
                    </div>
                    {selectedCandidate.alternative_assessment && selectedCandidate.alternative_assessment_note && (
                      <div className="mt-3 pt-3 border-t border-purple-200">
                        <strong className="text-sm text-purple-900">Begrunnelse:</strong>
                        <pre className="mt-1 text-xs text-gray-800 bg-white p-3 rounded border border-purple-100 whitespace-pre-wrap font-sans">
{selectedCandidate.alternative_assessment_note}
                        </pre>
                      </div>
                    )}
                    {selectedCandidate.sensor_comment && !selectedCandidate.alternative_assessment && (
                      <div className="mt-3 pt-3 border-t border-kf-blue-200">
                        <strong className="text-sm text-kf-blue-900">Sensor-kommentar:</strong>
                        <p className="mt-1 text-sm text-gray-800 bg-white p-3 rounded border border-kf-blue-100 whitespace-pre-wrap">
                          {selectedCandidate.sensor_comment}
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Question Overview */}
              {answers.length > 0 && (
                <div className="bg-white border rounded-lg p-4 mb-4">
                  <h4 className="text-sm font-semibold text-gray-900 mb-3">Spørsmålsoversikt</h4>
                  <div className="flex flex-wrap gap-2">
                    {answers.map((ans, idx) => {
                      const isGraded = ans.sensor_score !== null;
                      const isCurrent = idx === currentAnswerIndex;
                      return (
                        <button
                          key={ans.answer_id}
                          onClick={() => setCurrentAnswerIndex(idx)}
                          className={`w-10 h-10 rounded-lg text-sm font-medium transition ${
                            isCurrent
                              ? 'bg-kf-blue-600 text-white ring-2 ring-kf-blue-400'
                              : isGraded
                              ? 'bg-green-100 text-green-800 hover:bg-green-200'
                              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          }`}
                          title={`Spørsmål ${idx + 1} - ${isGraded ? 'Sensurert' : 'Ikke sensurert'}`}
                        >
                          {idx + 1}
                        </button>
                      );
                    })}
                  </div>
                  <div className="mt-2 text-xs text-gray-600">
                    <span className="inline-flex items-center gap-1 mr-3">
                      <span className="w-3 h-3 bg-green-100 border border-green-300 rounded"></span>
                      Sensurert
                    </span>
                    <span className="inline-flex items-center gap-1 mr-3">
                      <span className="w-3 h-3 bg-gray-100 border border-gray-300 rounded"></span>
                      Ikke sensurert
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <span className="w-3 h-3 bg-kf-blue-600 border border-kf-blue-600 rounded"></span>
                      Aktivt
                    </span>
                  </div>
                </div>
              )}

              {/* Answer Navigation */}
              {answers.length > 0 && (
                <div className="bg-white border rounded-lg p-4 mb-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <span className="text-sm font-medium text-gray-700">
                        {currentAnswerIndex >= answers.length
                          ? `📊 Oppsummering (etter spørsmål ${answers.length})`
                          : `Spørsmål ${currentAnswerIndex + 1} av ${answers.length}`}
                      </span>
                      <span className="text-xs text-gray-500">
                        Trykk 'N' for neste, 'P' for forrige
                      </span>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={goToPreviousAnswer}
                        disabled={currentAnswerIndex === 0}
                        className="px-3 py-1 text-sm border rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        ← Forrige
                      </button>
                      {(() => {
                        // v6.3.12: "Neste" fungerer også som "Til oppsummering" på siste svar
                        // hvis alle sensurert. Klikk → currentAnswerIndex = answers.length (viser summary).
                        const onLast = currentAnswerIndex === answers.length - 1;
                        const allGraded = answers.every(a => a.sensor_score !== null);
                        const onSummary = currentAnswerIndex === answers.length;
                        return (
                          <button
                            onClick={() => setCurrentAnswerIndex(currentAnswerIndex + 1)}
                            disabled={onSummary || (onLast && !allGraded)}
                            className={`px-3 py-1 text-sm border rounded disabled:opacity-50 disabled:cursor-not-allowed ${
                              onLast && allGraded ? 'bg-green-600 text-white border-green-700 hover:bg-green-700' : 'hover:bg-gray-50'
                            }`}
                            title={onLast && !allGraded ? 'Sensurer alle svar først for å gå til oppsummering' : ''}
                          >
                            {onLast && allGraded ? 'Til oppsummering →' : 'Neste →'}
                          </button>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              )}

              {/* v6.3.12: enten sensur-kort ELLER oppsummeringsside (siste "side" i navigasjonen) */}
              {currentAnswerIndex < answers.length && answers[currentAnswerIndex] && (
                <AnswerGradingCard
                  key={answers[currentAnswerIndex].answer_id}
                  answer={answers[currentAnswerIndex]}
                  index={currentAnswerIndex}
                  onSave={handleSaveGrading}
                  statistics={statistics}
                />
              )}

              {/* Oppsummerings-side (currentAnswerIndex === answers.length) */}
              {selectedCandidate.status !== 'graded' && currentAnswerIndex === answers.length && answers.length > 0 && (() => {
                // v6.3.12 Number-cast: sensor_score kommer som string fra Postgres numeric → må castes
                const totalPoints = answers.reduce((sum, a) => sum + Number(a.sensor_score ?? 0), 0);
                const maxPoints = answers.reduce((sum, a) => sum + Number(a.max_points ?? 0), 0);
                const percentage = maxPoints > 0 ? Math.round((totalPoints / maxPoints) * 100) : 0;
                const passThreshold = courseContext?.pass_threshold ?? 60;
                const passed = percentage >= passThreshold;

                // Karakter-preview basert på skala
                let previewGrade = '—';
                const scale = courseContext?.grading_scale;
                if (scale === 'pass_fail') previewGrade = passed ? 'Bestått' : 'Ikke bestått';
                else if (scale === 'three_tier') previewGrade = percentage >= 90 ? 'Meget godt bestått' : passed ? 'Bestått' : 'Ikke bestått';
                else if (scale === 'a_f') {
                  if (!passed) previewGrade = 'F';
                  else if (percentage >= 90) previewGrade = 'A';
                  else {
                    const width = (90 - passThreshold) / 4;
                    if (percentage >= passThreshold + width*3) previewGrade = 'B';
                    else if (percentage >= passThreshold + width*2) previewGrade = 'C';
                    else if (percentage >= passThreshold + width) previewGrade = 'D';
                    else previewGrade = 'E';
                  }
                } else previewGrade = `${percentage}%`;

                return (
                  <div className={`mt-6 rounded-lg border-2 p-6 ${passed ? 'bg-green-50 border-green-500' : 'bg-red-50 border-red-500'}`}>
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className={`text-xl font-bold ${passed ? 'text-green-900' : 'text-red-900'}`}>
                          {passed ? '✅ Klar til å fullføre — Bestått' : '❌ Klar til å fullføre — Ikke bestått'}
                        </h3>
                        <p className="text-sm text-gray-700 mt-1">
                          Alle {answers.length} svar er sensurert. Se oppsummering under og fullfør sensuren.
                        </p>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
                      <div className="bg-white rounded p-3 border">
                        <div className="text-xs text-gray-500 uppercase mb-1">Poengsum</div>
                        <div className="text-2xl font-bold text-gray-900">
                          {Number.isInteger(totalPoints) ? totalPoints : totalPoints.toFixed(1)} / {Number.isInteger(maxPoints) ? maxPoints : maxPoints.toFixed(1)}
                        </div>
                      </div>
                      <div className="bg-white rounded p-3 border">
                        <div className="text-xs text-gray-500 uppercase mb-1">Prosent</div>
                        <div className={`text-2xl font-bold ${passed ? 'text-green-700' : 'text-red-700'}`}>{percentage}%</div>
                      </div>
                      <div className="bg-white rounded p-3 border">
                        <div className="text-xs text-gray-500 uppercase mb-1">Karakter</div>
                        <div className={`text-2xl font-bold ${passed ? 'text-green-700' : 'text-red-700'}`}>{previewGrade}</div>
                      </div>
                      <div className="bg-white rounded p-3 border">
                        <div className="text-xs text-gray-500 uppercase mb-1">Terskel</div>
                        <div className="text-2xl font-bold text-gray-700">≥{passThreshold}%</div>
                      </div>
                    </div>

                    <div className={`text-sm p-3 rounded mb-4 ${passed ? 'bg-green-100 text-green-900' : 'bg-red-100 text-red-900'}`}>
                      {passed
                        ? `Kandidaten har oppnådd ${percentage}% som er over terskelen på ${passThreshold}%. Karakter: ${previewGrade}.`
                        : `Kandidaten har oppnådd ${percentage}% som er under terskelen på ${passThreshold}%. Karakter: ${previewGrade}.`}
                    </div>

                    <div className="flex flex-wrap gap-3">
                      <button
                        onClick={() => handleFinalizeGrading()}
                        disabled={finalizingGrading}
                        className={`px-6 py-3 rounded-lg text-white font-medium ${
                          passed ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'
                        } disabled:opacity-50`}
                      >
                        {finalizingGrading ? 'Fullfører…' : 'Fullfør sensur og send resultat til kandidat'}
                      </button>
                      <button
                        onClick={() => setCurrentAnswerIndex(0)}
                        className="px-4 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                      >
                        Gå tilbake til første svar for å gjennomgå
                      </button>
                    </div>
                    <p className="text-xs text-gray-600 mt-3">
                      Kandidat får e-post + SMS med resultat og claim-lenke til kompetanseattest ved fullføring.
                      Etter fullføring kan sensuren kun endres av systemadmin.
                    </p>
                  </div>
                );
              })()}
            </div>
          )}
        </div>
      </div>

      {/* Context Modal */}
      {showContextModal && courseContext && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b p-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">Studieplan og Sensureringskriterier</h2>
              <button
                onClick={() => setShowContextModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Exam Info */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <Info className="h-5 w-5 text-kf-blue-600" />
                  Prøveinformasjon
                </h3>
                <div className="bg-kf-blue-50 border border-kf-blue-200 rounded-lg p-4 space-y-2">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-kf-blue-700 font-medium">Prøvetittel:</p>
                      <p className="text-kf-blue-900">{courseContext.exam_title}</p>
                    </div>
                    <div>
                      <p className="text-sm text-kf-blue-700 font-medium">Beståelsesgrense:</p>
                      <p className="text-kf-blue-900">{courseContext.pass_threshold}%</p>
                    </div>
                    <div>
                      <p className="text-sm text-kf-blue-700 font-medium">Antall spørsmål:</p>
                      <p className="text-kf-blue-900">{courseContext.total_questions}</p>
                    </div>
                    <div>
                      <p className="text-sm text-kf-blue-700 font-medium">Totale poeng:</p>
                      <p className="text-kf-blue-900">{courseContext.total_points}</p>
                    </div>
                  </div>
                  {courseContext.exam_description && (
                    <div className="mt-3 pt-3 border-t border-kf-blue-300">
                      <p className="text-sm text-kf-blue-700 font-medium mb-1">Beskrivelse:</p>
                      <p className="text-kf-blue-900">{courseContext.exam_description}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Study Plan Info */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <BookOpen className="h-5 w-5 text-green-600" />
                  Studieplan
                </h3>
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 space-y-3">
                  <div>
                    <p className="text-sm text-green-700 font-medium">Tittel:</p>
                    <p className="text-green-900 font-semibold">{courseContext.study_plan_title}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-green-700 font-medium">Kompetansepoeng:</p>
                      <p className="text-green-900">{courseContext.ects_credits} kompetansepoeng</p>
                    </div>
                    <div>
                      <p className="text-sm text-green-700 font-medium">Estimerte timer:</p>
                      <p className="text-green-900">{courseContext.estimated_hours} timer</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Learning Outcomes */}
              {courseContext.learning_outcomes && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Læringsutbytte</h3>
                  <div className="space-y-4">
                    {courseContext.learning_outcomes.knowledge?.length > 0 && (
                      <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                        <p className="text-sm font-semibold text-purple-900 mb-2">Kunnskap:</p>
                        <ul className="list-disc list-inside space-y-1 text-sm text-purple-800">
                          {courseContext.learning_outcomes.knowledge.map((item, idx) => (
                            <li key={idx}>{item}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {courseContext.learning_outcomes.skills?.length > 0 && (
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                        <p className="text-sm font-semibold text-amber-900 mb-2">Ferdigheter:</p>
                        <ul className="list-disc list-inside space-y-1 text-sm text-amber-800">
                          {courseContext.learning_outcomes.skills.map((item, idx) => (
                            <li key={idx}>{item}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {courseContext.learning_outcomes.competence?.length > 0 && (
                      <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-4">
                        <p className="text-sm font-semibold text-cyan-900 mb-2">Generell kompetanse:</p>
                        <ul className="list-disc list-inside space-y-1 text-sm text-cyan-800">
                          {courseContext.learning_outcomes.competence.map((item, idx) => (
                            <li key={idx}>{item}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Assessment Criteria */}
              {courseContext.assessment_criteria && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Vurderingskriterier</h3>
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <p className="text-sm text-amber-900 whitespace-pre-wrap">
                      {courseContext.assessment_criteria}
                    </p>
                  </div>
                </div>
              )}

              {/* Content Modules */}
              {courseContext.content_modules?.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Innholdsmoduler</h3>
                  <div className="space-y-3">
                    {courseContext.content_modules.map((module, idx) => (
                      <div key={idx} className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                        <div className="flex items-start justify-between mb-2">
                          <p className="font-semibold text-gray-900">{module.title}</p>
                          <span className="text-sm text-gray-600">{module.hours} timer</span>
                        </div>
                        <p className="text-sm text-gray-700">{module.description}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="sticky bottom-0 bg-white border-t p-4 flex justify-end">
              <button
                onClick={() => setShowContextModal(false)}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
              >
                Lukk
              </button>
            </div>
          </div>
        </div>
      )}

      {/* v6.3.1 — Alternativ vurderings-modal */}
      {showAlternativeModal && selectedCandidate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-purple-50 border-b border-purple-200 px-6 py-4 flex justify-between items-center">
              <div>
                <h2 className="text-xl font-bold text-purple-900">Registrer alternativ vurdering</h2>
                <p className="text-sm text-purple-700 mt-1">Kandidatkode: {selectedCandidate.candidate_code}</p>
              </div>
              <button
                onClick={() => { setShowAlternativeModal(false); setAltJustification(''); setAltConfirmChecked(false); setError(''); }}
                className="text-gray-400 hover:text-gray-600"
                aria-label="Lukk"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="p-6 space-y-5">
              <div className="bg-amber-50 border border-amber-300 rounded p-3 text-sm text-amber-900">
                <strong>Bruk kun når skriftlig prøve er utilstrekkelig eller ikke gjennomførbart</strong> —
                typisk ved teknisk feil, tilrettelegging, eller når kandidaten er vurdert muntlig/praktisk
                i stedet. Attesten vil eksplisitt merkes med at vurderingen ble gjort alternativt.
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Type alternativ vurdering</label>
                <select
                  value={altAssessmentType}
                  onChange={(e) => setAltAssessmentType(e.target.value as any)}
                  className="w-full border rounded-lg px-3 py-2"
                >
                  <option value="oral">Muntlig vurdering</option>
                  <option value="observation">Observasjon</option>
                  <option value="practical">Praktisk demonstrasjon</option>
                  <option value="other">Annet</option>
                </select>
              </div>

              {/* v6.3.1 — Karakter låst til prøvesettets skala + eksplisitt grense-info */}
              <div>
                <div className="flex items-baseline justify-between mb-2">
                  <label className="text-sm font-medium text-gray-700">
                    Karakter <span className="text-red-600">*</span>
                  </label>
                  <span className="text-xs text-gray-500">
                    Skala: <code className="bg-gray-100 px-1 rounded">{courseContext?.grading_scale ?? 'ukjent'}</code>
                    {' · '}Bestått ved ≥{courseContext?.pass_threshold}%
                  </span>
                </div>

                {gradeOpts?.numeric && (
                  <>
                    <input
                      type="number"
                      min={0}
                      max={100}
                      value={altGrade}
                      onChange={(e) => setAltGrade(e.target.value)}
                      className="w-32 border rounded-lg px-3 py-2"
                    />
                    <span className="ml-2 text-sm text-gray-600">%</span>
                  </>
                )}

                {gradeOpts && !gradeOpts.numeric && (
                  <div className="flex flex-wrap gap-2">
                    {gradeOpts.passing.map(g => (
                      <button
                        key={g}
                        type="button"
                        onClick={() => setAltGrade(g)}
                        className={`px-3 py-2 rounded-lg border text-sm transition ${
                          altGrade === g
                            ? 'bg-green-600 text-white border-green-700 ring-2 ring-green-300'
                            : 'bg-green-50 text-green-800 border-green-300 hover:bg-green-100'
                        }`}
                      >
                        {g}
                      </button>
                    ))}
                    {gradeOpts.failing.map(g => (
                      <button
                        key={g}
                        type="button"
                        onClick={() => setAltGrade(g)}
                        className={`px-3 py-2 rounded-lg border text-sm transition ${
                          altGrade === g
                            ? 'bg-red-600 text-white border-red-700 ring-2 ring-red-300'
                            : 'bg-red-50 text-red-800 border-red-300 hover:bg-red-100'
                        }`}
                      >
                        {g}
                      </button>
                    ))}
                  </div>
                )}

                {gradeOpts && !gradeOpts.numeric && (
                  <div className="mt-3 text-xs bg-kf-blue-50 border border-kf-blue-200 rounded p-2 text-kf-blue-900">
                    <strong>Trinn på {courseContext?.grading_scale}-skalaen:</strong>
                    <ul className="mt-1 space-y-0.5 ml-4 list-disc">
                      {gradeOpts.passing.map(g => (
                        <li key={g}><span className="text-green-700 font-medium">{g}</span> — bestått</li>
                      ))}
                      {gradeOpts.failing.map(g => (
                        <li key={g}><span className="text-red-700 font-medium">{g}</span> — ikke bestått</li>
                      ))}
                    </ul>
                    <p className="mt-1">Grense for bestått er satt til <strong>{courseContext?.pass_threshold}%</strong> på prøvesettet.</p>
                  </div>
                )}

                {altGrade && (
                  <div className={`mt-2 text-sm ${isSelectedPassing ? 'text-green-700' : 'text-red-700'}`}>
                    Valgt: <strong>{altGrade}</strong> → {isSelectedPassing ? '✅ Bestått' : '❌ Ikke bestått'}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Begrunnelse <span className="text-red-600">*</span>
                  <span className="text-xs text-gray-500 ml-2">
                    ({altJustification.length} / min 100 tegn)
                  </span>
                </label>
                <textarea
                  value={altJustification}
                  onChange={(e) => setAltJustification(e.target.value)}
                  rows={6}
                  className={`w-full border rounded-lg px-3 py-2 text-sm ${
                    altJustification.length < 100 ? 'border-amber-400' : 'border-green-400'
                  }`}
                  placeholder="Beskriv hva som ble vurdert (spørsmål/oppgaver), hvordan (muntlig/praktisk/observasjon), hvor og når, og hvorfor du konkluderer med bestått eller ikke bestått. Denne begrunnelsen lagres i audit-log og er en del av attestens dokumentasjon."
                />
              </div>

              <label className="flex items-start gap-2 bg-gray-50 border rounded p-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={altConfirmChecked}
                  onChange={(e) => setAltConfirmChecked(e.target.checked)}
                  className="mt-0.5"
                />
                <span className="text-sm text-gray-700">
                  <strong>Jeg bekrefter</strong> at jeg har gjennomført alternativ vurdering av denne
                  kandidaten personlig, og at begrunnelsen over reflekterer sannheten. Jeg er
                  klar over at handlingen loggføres med mitt bruker-id og ikke kan reverseres uten
                  systemadmin.
                </span>
              </label>

              {error && (
                <div className="bg-red-50 border border-red-300 rounded p-3 text-sm text-red-800">{error}</div>
              )}

              <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                  onClick={() => { setShowAlternativeModal(false); setAltJustification(''); setAltConfirmChecked(false); setError(''); }}
                  className="px-4 py-2 border rounded-lg text-gray-700 hover:bg-gray-50"
                  disabled={savingAlternative}
                >
                  Avbryt
                </button>
                <button
                  onClick={handleSubmitAlternative}
                  disabled={savingAlternative || !altGrade || altJustification.trim().length < 100 || !altConfirmChecked}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {savingAlternative && <Loader2 className="h-4 w-4 animate-spin" />}
                  Registrer alternativ vurdering ({altGrade || 'velg karakter'})
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Answer grading card component
function AnswerGradingCard({
  answer,
  index,
  onSave,
  statistics
}: {
  answer: Answer;
  index: number;
  onSave: (answerId: string, score: number, feedback: string, acceptAI: boolean) => void;
  statistics: QuestionStatistics[];
}) {
  const toast = useToast();
  // Find statistics for this question
  const questionStats = statistics.find(s => s.question_text === answer.question_text);
  // Auto-score for objective questions
  const getAutoScore = () => {
    if (answer.question_type === 'multiple_choice' || answer.question_type === 'true_false') {
      if (answer.selected_option !== null && answer.options) {
        const isCorrect = answer.options[answer.selected_option]?.is_correct;
        return isCorrect ? answer.max_points : 0;
      }
    }
    return null;
  };

  const autoScore = getAutoScore();
  const initialScore = answer.sensor_score?.toString() || (autoScore !== null ? autoScore.toString() : '');

  const [score, setScore] = useState(initialScore);
  const [feedback, setFeedback] = useState(answer.sensor_feedback || '');
  const [useAI, setUseAI] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'saved' | 'saving' | null>(null);

  const isGraded = answer.sensor_score !== null;
  const hasAI = answer.ai_suggested_score !== null;
  const isObjectiveQuestion = answer.question_type === 'multiple_choice' || answer.question_type === 'true_false';
  const isAutoScored = isObjectiveQuestion && answer.sensor_score !== null &&
                       (answer.sensor_feedback?.includes('Automatisk sensurert') || false);

  // Auto-save after 2 seconds of inactivity
  useEffect(() => {
    if (score === initialScore && feedback === (answer.sensor_feedback || '')) {
      return; // No changes
    }

    const scoreValue = parseFloat(score);
    if (isNaN(scoreValue) || scoreValue < 0 || scoreValue > answer.max_points) {
      return; // Invalid score
    }

    const timer = setTimeout(() => {
      setSaveStatus('saving');
      setIsSaving(true);
      onSave(answer.answer_id, scoreValue, feedback, useAI);
      setTimeout(() => {
        setSaveStatus('saved');
        setIsSaving(false);
        setTimeout(() => setSaveStatus(null), 2000);
      }, 500);
    }, 2000);

    return () => clearTimeout(timer);
  }, [score, feedback]);

  const handleSubmit = () => {
    const scoreValue = parseFloat(score);
    if (isNaN(scoreValue) || scoreValue < 0 || scoreValue > answer.max_points) {
      toast.showWarning(`Score må være mellom 0 og ${answer.max_points}`);
      return;
    }
    onSave(answer.answer_id, scoreValue, feedback, useAI);
  };

  const applyAISuggestion = () => {
    if (hasAI) {
      setScore(answer.ai_suggested_score!.toString());
      setFeedback(answer.ai_suggested_feedback || '');
      setUseAI(true);
    }
  };

  return (
    <div className={`bg-white border rounded-lg p-6 ${isGraded ? 'border-green-200' : ''}`}>
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <span className="flex items-center justify-center w-8 h-8 bg-kf-blue-100 text-kf-blue-600 rounded-full font-semibold text-sm">
            {index + 1}
          </span>
          <div>
            <span className="text-xs text-gray-500 uppercase">
              {answer.question_type}
            </span>
            <p className="font-medium text-gray-900">Maks: {answer.max_points} poeng</p>
          </div>
        </div>
        {isGraded && (
          <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-semibold rounded-full flex items-center gap-1">
            <CheckCircle className="h-3 w-3" />
            Sensurert
          </span>
        )}
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Spørsmål:</label>
          <p className="text-gray-900 bg-gray-50 p-3 rounded border">
            {answer.question_text}
          </p>
          {answer.grading_criteria && (
            <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded">
              <p className="text-xs font-semibold text-amber-900 uppercase mb-1">
                Sensureringskriterier:
              </p>
              <p className="text-sm text-amber-900 whitespace-pre-wrap">
                {answer.grading_criteria}
              </p>
            </div>
          )}
        </div>

        {/* Statistics and Quality Assurance */}
        {questionStats && questionStats.graded_count > 0 && (
          <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
            <div className="flex items-start gap-2 mb-3">
              <Info className="h-5 w-5 text-indigo-600 mt-0.5" />
              <div className="flex-1">
                <h4 className="font-medium text-indigo-900 mb-1">Statistikk for dette spørsmålet</h4>
                <p className="text-xs text-indigo-700">
                  Basert på {questionStats.graded_count} sensurerte svar av {questionStats.total_answers} totalt
                </p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-white rounded p-2">
                <p className="text-xs text-gray-600">Gjennomsnitt</p>
                <p className="text-lg font-semibold text-indigo-900">
                  {questionStats.avg_score ? parseFloat(questionStats.avg_score).toFixed(1) : '0'}/{answer.max_points}
                </p>
                <p className="text-xs text-gray-500">
                  {questionStats.avg_score ? ((parseFloat(questionStats.avg_score) / answer.max_points) * 100).toFixed(0) : '0'}%
                </p>
              </div>
              <div className="bg-white rounded p-2">
                <p className="text-xs text-gray-600">Min - Maks</p>
                <p className="text-lg font-semibold text-indigo-900">
                  {questionStats.min_score ? parseFloat(questionStats.min_score).toFixed(1) : '0'} - {questionStats.max_score ? parseFloat(questionStats.max_score).toFixed(1) : '0'}
                </p>
              </div>
              {questionStats.std_dev && parseFloat(questionStats.std_dev) > 0 && (
                <div className="bg-white rounded p-2 col-span-2">
                  <p className="text-xs text-gray-600">Standardavvik</p>
                  <p className="text-lg font-semibold text-indigo-900">
                    ±{parseFloat(questionStats.std_dev).toFixed(2)}
                  </p>
                  <p className="text-xs text-gray-500">
                    {parseFloat(questionStats.std_dev) > (answer.max_points * 0.3) ?
                      '⚠️ Stor variasjon i sensurering' :
                      '✓ Konsistent sensurering'}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Kandidatens svar:</label>
          {answer.question_type === 'written' ? (
            <p className="text-gray-900 bg-kf-blue-50 p-3 rounded border border-kf-blue-200 whitespace-pre-wrap">
              {answer.candidate_answer || 'Ingen svar gitt'}
            </p>
          ) : (
            <div className="bg-kf-blue-50 p-3 rounded border border-kf-blue-200">
              {answer.selected_option !== null && answer.options ? (
                <div className="space-y-2">
                  <p className="font-medium text-kf-blue-900">
                    Valgt alternativ {answer.selected_option}:
                  </p>
                  <p className="text-gray-900">
                    {answer.options[answer.selected_option]?.text || 'Alternativ ikke funnet'}
                  </p>
                  <p className="text-sm mt-2">
                    {answer.options[answer.selected_option]?.is_correct ? (
                      <span className="text-green-700 font-medium">✓ Riktig svar</span>
                    ) : (
                      <span className="text-red-700 font-medium">✗ Feil svar</span>
                    )}
                  </p>
                </div>
              ) : (
                <p className="text-gray-600">Ingen svar gitt</p>
              )}
            </div>
          )}
        </div>

        {isAutoScored && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-start gap-2">
              <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
              <div className="flex-1">
                <h4 className="font-medium text-green-900 mb-1">Automatisk sensurert</h4>
                <div className="space-y-2">
                  <div>
                    <span className="text-sm text-green-700">Poeng: </span>
                    <span className="font-semibold text-green-900">
                      {answer.sensor_score}/{answer.max_points}
                    </span>
                  </div>
                  <p className="text-sm text-green-800">
                    {answer.sensor_score === answer.max_points
                      ? '✓ Korrekt svar - full poengsum'
                      : '✗ Feil svar - ingen poeng'}
                  </p>
                  <p className="text-xs text-green-700 mt-2">
                    Objektive spørsmål sensureres automatisk. Du kan overstyre ved å endre poengsummen nedenfor.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {hasAI && (
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <div className="flex items-start gap-2 mb-2">
              <Sparkles className="h-5 w-5 text-purple-600 mt-0.5" />
              <div className="flex-1">
                <h4 className="font-medium text-purple-900 mb-1">KI-forslag</h4>
                <div className="space-y-2">
                  <div>
                    <span className="text-sm text-purple-700">Poeng: </span>
                    <span className="font-semibold text-purple-900">
                      {answer.ai_suggested_score}/{answer.max_points}
                    </span>
                  </div>
                  <div>
                    <span className="text-sm text-purple-700">Tilbakemelding: </span>
                    <p className="text-sm text-purple-900 mt-1">
                      {answer.ai_suggested_feedback}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <button
              onClick={applyAISuggestion}
              className="mt-2 text-sm text-purple-700 hover:text-purple-900 underline"
            >
              Bruk KI-forslag
            </button>
          </div>
        )}

        {/* Only show grading fields for non-auto-scored questions */}
        {!isAutoScored && (
          <>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Poeng *
                </label>
                <input
                  type="number"
                  min="0"
                  max={answer.max_points}
                  step="0.5"
                  value={score}
                  onChange={(e) => {
                    setScore(e.target.value);
                    setUseAI(false);
                  }}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-kf-blue-500"
                  placeholder={`0 - ${answer.max_points}`}
                />
              </div>
            </div>

            {/* Consistency Warning */}
            {questionStats && questionStats.graded_count > 2 && score && !isNaN(parseFloat(score)) && questionStats.avg_score && (
              (() => {
                const currentScore = parseFloat(score);
                const avgScore = parseFloat(questionStats.avg_score);
                const deviation = Math.abs(currentScore - avgScore);
                const stdDev = questionStats.std_dev ? parseFloat(questionStats.std_dev) : 0;
                const threshold = stdDev > 0 ? stdDev * 1.5 : answer.max_points * 0.3;

                if (deviation > threshold) {
                  const direction = currentScore > avgScore ? 'høyere' : 'lavere';
                  return (
                    <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3 rounded">
                      <div className="flex items-start gap-2">
                        <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5" />
                        <div className="flex-1">
                          <h4 className="text-sm font-semibold text-yellow-900">Konsistensadvarsel</h4>
                          <p className="text-sm text-yellow-800 mt-1">
                            Din score ({currentScore}) er betydelig {direction} enn gjennomsnittet ({avgScore?.toFixed(1)}).
                            Avvik: {deviation.toFixed(1)} poeng.
                          </p>
                          <p className="text-xs text-yellow-700 mt-1">
                            Vurder om scoren er korrekt i forhold til kriteriene.
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                }
                return null;
              })()
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tilbakemelding (valgfritt)
              </label>
              <textarea
                value={feedback}
                onChange={(e) => {
                  setFeedback(e.target.value);
                  setUseAI(false);
                }}
                rows={3}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-kf-blue-500"
                placeholder="Gi tilbakemelding til kandidaten..."
              />
            </div>

            <div className="space-y-2">
              {saveStatus && (
                <div className={`text-sm text-center ${saveStatus === 'saving' ? 'text-kf-blue-600' : 'text-green-600'}`}>
                  {saveStatus === 'saving' ? '💾 Lagrer...' : '✓ Lagret'}
                </div>
              )}
            </div>
          </>
        )}

        {/* Show option to override auto-score if needed */}
        {isAutoScored && (
          <div className="mt-4">
            <details className="text-sm">
              <summary className="cursor-pointer text-gray-600 hover:text-gray-900">
                Trenger du å overstyre automatisk sensurering?
              </summary>
              <div className="mt-3 space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Poeng (overstyr)
                  </label>
                  <input
                    type="number"
                    min="0"
                    max={answer.max_points}
                    step="0.5"
                    value={score}
                    onChange={(e) => {
                      setScore(e.target.value);
                      setUseAI(false);
                    }}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-kf-blue-500"
                    placeholder={`0 - ${answer.max_points}`}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Begrunnelse for overstyring
                  </label>
                  <textarea
                    value={feedback}
                    onChange={(e) => {
                      setFeedback(e.target.value);
                      setUseAI(false);
                    }}
                    rows={2}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-kf-blue-500"
                    placeholder="Forklar hvorfor du overstyrer automatisk sensurering..."
                  />
                </div>
                <button
                  onClick={handleSubmit}
                  disabled={!score || isSaving}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Save className="h-4 w-4" />
                  Lagre overstyring
                </button>
              </div>
            </details>
          </div>
        )}
      </div>
    </div>
  );
}
